<script setup>
import dayjs from 'dayjs';
import { computed } from 'vue';
import verifiedIcon from '@/assets/verified-icon.svg';
import unverifiedIcon from '@/assets/unverified-icon.svg';
import insuredIcon from '@/assets/insured-icon.svg';
import coiNotFoundIcon from '@/assets/coi-not-found-icon.svg';
import { useScreenSize } from '@/composables';
import { useStore } from 'vuex';
import MyLoadsDesktopButtonComponent from '@/components/myLoads/MyLoadsDesktopButtonComponent.vue';

const { innerWidth } = useScreenSize();

const store = useStore();
const props = defineProps({
  equipment: {
    type: Object,
    default: () => ({}),
  },
  isModal: {
    type: Boolean,
    default: false,
  },
});

const isSetEldExemptFeatureEnabled = computed(
  () =>
    store.state.users.featureFlags
      ?.fe_carrier_set_carrier_vehicle_eld_exempt_status?.enabled
);

const emit = defineEmits(['resolveIssuesClick']);

const formattedLastSeen = computed(() =>
  dayjs(props.equipment.last_seen).format('MM/DD/YYYY')
);
</script>

<template>
  <!--     class="flex  flex-col gap-2 rounded-xl bg-white px-4 py-3 outline outline-1 outline-offset-[-1px] outline-neutral-300-a"
 -->
  <div
    v-if="innerWidth > 0"
    class="flex h-[285px] min-w-[360px] max-w-[600px] flex-1 flex-col rounded-xl border border-steel-40 bg-white"
    :class="
      innerWidth < 400 ? 'w-full' : innerWidth < 610 ? 'w-[400px]' : 'w-[285px]'
    "
  >
    <div
      class="flex items-center justify-between border-b border-steel-40 px-4 py-2"
    >
      <div class="flex items-center gap-1">
        <img
          class=""
          :src="equipment.verified ? verifiedIcon : unverifiedIcon"
          :alt="equipment.verified ? 'Verified Icon' : 'Unverified Icon'"
        />
        <span class="text-steel-1100">
          {{ equipment.verified ? 'Verified' : 'Unverified' }}
        </span>
      </div>
      <p class="font-medium text-steel-1100">
        <span class="text-sm text-steel-700">VIN</span>
        {{ equipment.vin }}
      </p>
    </div>
    <div class="flex h-full flex-col">
      <div
        class="flex h-full flex-col justify-center px-4 py-2"
        :class="
          isSetEldExemptFeatureEnabled &&
          !isModal &&
          !equipment.is_eld_and_active &&
          !equipment.is_eld_exempt
            ? 'gap-2'
            : 'gap-4'
        "
      >
        <div class="grid grid-cols-4 text-left">
          <span class="col-span-1 text-sm font-normal text-steel-700">
            Truck #
          </span>
          <span class="col-span-3 truncate text-sm font-medium text-steel-900">
            {{ equipment.truck_number }}
          </span>
        </div>
        <div class="grid grid-cols-4 text-left">
          <span class="col-span-1 text-sm font-normal text-steel-700">
            Vehicle
          </span>
          <span class="col-span-3 truncate text-sm font-medium text-steel-900">
            {{
              [equipment?.year, equipment?.model, equipment?.make]
                .filter(Boolean)
                .join(' ')
            }}
          </span>
        </div>
        <div class="grid grid-cols-4 text-left">
          <span class="col-span-1 text-sm font-normal text-steel-700">
            Source
          </span>
          <span class="col-span-3 truncate text-sm font-medium text-steel-900">
            {{ equipment.source }}
          </span>
        </div>
        <div class="grid grid-cols-4 text-left">
          <span class="col-span-1 text-sm font-normal text-steel-700">
            Last Seen
          </span>
          <span class="col-span-3 truncate text-sm font-medium text-steel-900">
            {{ formattedLastSeen }}
          </span>
        </div>
      </div>
      <div class="flex flex-col gap-2 rounded-b-xl bg-steel-100 p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <span class="mr-2 text-sm text-steel-700">ELD</span>
            <img
              v-if="equipment.is_eld_and_active"
              src="@/assets/eld-connected-icon.svg"
              alt="ELD Connected Icon"
            />
            <img
              v-else-if="equipment.is_eld_exempt"
              class="h-4 w-4"
              src="@/assets/eld-exempt-icon.svg"
              alt="ELD Exempt Icon"
            />
            <img
              v-else
              src="@/assets/eld-not-connected-icon.svg"
              alt="ELD Not Connected"
            />
            <p class="ml-1">
              {{
                equipment.is_eld_and_active
                  ? 'Connected'
                  : equipment.eld_exemption_type?.includes('pending')
                    ? 'Exemption Pending'
                    : equipment.eld_exemption_type?.includes('denied')
                      ? 'Exemption Denied'
                      : equipment.eld_exemption_type
                        ? 'Exempt'
                        : 'Not Connected'
              }}
            </p>
          </div>
          <div class="flex items-center">
            <span class="mr-2 text-sm text-steel-700">COI</span>
            <img
              :src="equipment.is_coi ? insuredIcon : coiNotFoundIcon"
              :alt="equipment.is_coi ? 'Insured Icon' : 'COI Not Found Icon'"
            />
            <p class="ml-1">
              {{ equipment.is_coi ? 'Insured' : 'Not Found' }}
            </p>
          </div>
        </div>
        <div
          v-if="
            isSetEldExemptFeatureEnabled &&
            !isModal &&
            !equipment.is_eld_and_active &&
            !equipment.eld_exemption_type
          "
          class="flex h-12 w-full justify-center"
        >
          <div class="flex w-full items-center justify-between gap-2">
            <MyLoadsDesktopButtonComponent
              button-class="flex-1 default-button rounded-[3px] px-3 py-1.5"
              button-text="Resolve Issues"
              text-class=" text-sm font-medium leading-10 tracking-tight"
              @button-click="emit('resolveIssuesClick', equipment)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
